"use client";

import submitApplicationForm, {
  ApplicationFormState,
} from "@/actions/submitApplicationForm";
import { resolveImageUrl, resolveSectionClasses } from "@/storyblok/resolvers";
import { ApplicationFormBlockData } from "@/storyblok/schemas";
import { makeEditable } from "@/storyblok/visual-editor";
import { renderRichText } from "@storyblok/js";
import { useEffect, useRef } from "react";
import { useFormState } from "react-dom";

export default function ApplicationForm({
  data,
}: {
  data: ApplicationFormBlockData;
}) {
  const fields = [
    ...(data.show_name ? ["firstname", "lastname"] : []),
    ...(data.show_email ? ["email"] : []),
    ...(data.show_phone ? ["phone"] : []),
    ...(data.show_cv ? ["cv"] : []),
    ...(data.show_motivation ? ["motivation"] : []),
    ...(data.show_remarks ? ["remarks"] : []),
    "confirmation",
  ];

  const formRef = useRef<HTMLFormElement | null>(null);
  const [state, formAction] = useFormState<ApplicationFormState>(
    // @ts-ignore
    submitApplicationForm.bind(null, {
      recruiterEmail: data.recruiter_email,
      fields,
    }),
    { status: "idle" }
  );

  useEffect(() => {
    if (state.status == "success") {
      formRef.current?.reset();
    }
  }, [state]);

  return (
    <section
      id={data.anchor}
      className="section--contact-form"
      style={{
        "--color-background": data.background_color?.color,
      }}
      {...makeEditable(data)}
    >
      <div className={resolveSectionClasses(data, "container")}>
        <div className="col">
          {data.image && (
            <img
              className="img_mobile"
              src={resolveImageUrl(data.image, { width: 460 })}
              alt={data.image.alt}
            />
          )}
          <h3>{data.title}</h3>
          <div
            dangerouslySetInnerHTML={{ __html: renderRichText(data.content) }}
          ></div>
          {data.image && (
            <img
              className="img_desktop"
              src={resolveImageUrl(data.image)}
              alt={data.image.alt}
            />
          )}
        </div>
        <div className="col">
          <form action={formAction} ref={formRef}>
            <fieldset>
              {data.show_name && (
                <>
                  <div
                    className={`form_col w50 ${
                      state.status === "error" && state.errors.firstname
                        ? "error"
                        : ""
                    }`}
                  >
                    <label htmlFor="firstname">
                      <span>Voornaam</span> <span aria-label="required">*</span>
                    </label>
                    <input
                      id="firstname"
                      type="text"
                      name="firstname"
                      placeholder="Voornaam"
                      required
                    />
                    {state.status === "error" && state.errors.firstname && (
                      <span id="" className="error_msg">
                        Vul je voornaam in
                      </span>
                    )}
                  </div>
                  <div
                    className={`form_col w50 ${
                      state.status === "error" && state.errors.lastname
                        ? "error"
                        : ""
                    }`}
                  >
                    <label htmlFor="lastname">
                      <span>Achternaam</span>
                      <span aria-label="required">*</span>
                    </label>
                    <input
                      id="lastname"
                      type="text"
                      name="lastname"
                      placeholder="Achternaam"
                      required
                    />
                    {state.status === "error" && state.errors.lastname && (
                      <span id="" className="error_msg">
                        Vul je achternaam in
                      </span>
                    )}
                  </div>
                </>
              )}
              {data.show_email && (
                <div
                  className={`form_col ${
                    state.status === "error" && state.errors.email
                      ? "error"
                      : ""
                  }`}
                >
                  <label htmlFor="email">
                    <span>E-mailadres</span>
                    <span aria-label="required">*</span>
                  </label>
                  <input
                    id="email"
                    type="text"
                    name="email"
                    placeholder="E-mailadres"
                    required
                  />
                  {state.status === "error" && state.errors.email && (
                    <span id="" className="error_msg">
                      Vul een geldig e-mailadres in
                    </span>
                  )}
                </div>
              )}
              {data.show_phone && (
                <div
                  className={`form_col ${
                    state.status === "error" && state.errors.phone
                      ? "error"
                      : ""
                  }`}
                >
                  <label htmlFor="phone">
                    <span>Telefoonnummer</span>
                    <span aria-label="required">*</span>
                  </label>
                  <input
                    id="phone"
                    type="text"
                    name="phone"
                    placeholder="Telefoonnummer"
                    required
                  />
                  {state.status === "error" && state.errors.phone && (
                    <span id="" className="error_msg">
                      Vul een geldig telefoonnummer in
                    </span>
                  )}
                </div>
              )}
              {data.show_cv && (
                <div
                  className={`form_col ${
                    state.status === "error" && state.errors.cv ? "error" : ""
                  }`}
                >
                  <label htmlFor="cv">
                    <span>Upload je CV</span>
                    <span aria-label="required">*</span>
                  </label>
                  <input
                    id="cv"
                    type="file"
                    accept=".doc, .docx, .pdf, .txt"
                    name="cv"
                    placeholder="Upload je CV"
                    required
                  />
                  {state.status === "error" && state.errors.cv && (
                    <span id="" className="error_msg">
                      Kies een bestand (.doc, .docx, .pdf, .txt)
                    </span>
                  )}
                </div>
              )}
              {data.show_motivation && (
                <div
                  className={`form_col ${
                    state.status === "error" && state.errors.motivation
                      ? "error"
                      : ""
                  }`}
                >
                  <label htmlFor="motivation">
                    <span>Upload je motivatiebrief</span>
                    <span aria-label="required">*</span>
                  </label>
                  <input
                    id="motivation"
                    type="file"
                    accept=".doc, .docx, .pdf, .txt"
                    name="motivation"
                    placeholder="Upload je motivatiebrief"
                    required
                  />
                  {state.status === "error" && state.errors.motivation && (
                    <span id="" className="error_msg">
                      Kies een bestand (.doc, .docx, .pdf, .txt)
                    </span>
                  )}
                </div>
              )}
              {data.show_remarks && (
                <div
                  className={`form_col ${
                    state.status === "error" && state.errors.remarks
                      ? "error"
                      : ""
                  }`}
                >
                  <label htmlFor="remarks">
                    <span>Opmerkingen of vragen</span>
                  </label>
                  <textarea
                    id="remarks"
                    name="remarks"
                    placeholder="Opmerkingen of vragen"
                  ></textarea>
                  {state.status === "error" && (
                    <span id="" className="error_msg">
                      Vul je vragen of opmerkingen in
                    </span>
                  )}
                </div>
              )}
              <div
                className={`form_col optin ${
                  state.status === "error" && state.errors.confirmation
                    ? "error"
                    : ""
                }`}
              >
                <input
                  type="checkbox"
                  id="confirmation"
                  name="confirmation"
                  value="yes"
                />
                <label htmlFor="confirmation">
                  <span>
                    Ik ga akkoord met de
                    <a href="#!">&nbsp;voorwaarden en privacyrichtlijn</a>.
                    <span aria-label="required">*</span>
                  </span>
                </label>
                {state.status === "error" && state.errors.confirmation && (
                  <span id="" className="error_msg">
                    Je moet akkoord gaan met onze voorwaarden
                  </span>
                )}
              </div>
              <div className="form_col">
                <button className="submit" type="submit">
                  {state.status === "success" ? "Verzonden!" : "Verzenden"}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </section>
  );
}
